<template lang="pug">
main.booking-content(:class="classes")
  .booking-inner

    .step.type-step(v-if="step === 1")
      h2.step-title Schedule a meeting
      p.step-sub Choose a meeting type to get started.
      .loading-msg(v-if="loadingTypes") Loading...
      .error-msg(v-else-if="typesError") Failed to load meeting types. Please try again later.
      .type-cards(v-else)
        .type-card(
          v-for="type in bookingTypes"
          :key="type.id"
          @click="selectType(type)"
        )
          .type-duration {{ type.duration_minutes }} min
          .type-name {{ type.title }}
          .type-desc(v-if="type.description") {{ stripHtml(type.description) }}

    .step.calendar-step(v-if="step === 2")
      h2.step-title Pick a date
      p.step-sub {{ selectedType?.title }}
      .calendar
        .cal-header
          button.cal-nav(@click="prevMonth") ‹
          span.cal-month {{ monthYear }}
          button.cal-nav(@click="nextMonth") ›
        .cal-grid
          .day-label(v-for="d in ['Su','Mo','Tu','We','Th','Fr','Sa']" :key="d") {{ d }}
          button.cal-day(
            v-for="(day, i) in calendarDays"
            :key="i"
            :class="{ empty: !day.date, today: day.isToday, past: day.isPast, selected: day.isSelected, available: day.date && !day.isPast }"
            :disabled="!day.date || day.isPast"
            @click="day.date && !day.isPast && selectDate(day.date)"
          ) {{ day.label }}
      button.back-link(@click="step = 1") ← Back

    .step.slots-step(v-if="step === 3")
      h2.step-title Available times
      p.step-sub {{ selectedDateFormatted }}
      .loading-msg(v-if="loadingSlots") Loading available times...
      .no-slots(v-else-if="!loadingSlots && timeslots.length === 0")
        p No available times on this date.
        button.back-link(@click="step = 2") ← Pick another date
      .slots-grid(v-else)
        button.slot-btn(
          v-for="slot in timeslots"
          :key="slot.starts_at"
          :class="{ selected: selectedSlot?.starts_at === slot.starts_at }"
          @click="selectSlot(slot)"
        ) {{ formatTime(slot.starts_at) }}
      button.back-link(v-if="timeslots.length > 0" @click="step = 2") ← Back

    .step.form-step(v-if="step === 4")
      h2.step-title Your details
      .booking-summary
        span {{ selectedType?.title }}
        span.dot &nbsp;·&nbsp;
        span {{ selectedDateFormatted }}
        span.dot &nbsp;·&nbsp;
        span {{ formatTime(selectedSlot?.starts_at) }}
      form(@submit.prevent="submitBooking")
        .form-field
          label Name
          input(v-model="form.name" type="text" required placeholder="Your full name")
        .form-field
          label Email
          input(v-model="form.email" type="email" required placeholder="your@email.com")
        .form-field
          label Timezone
          select(v-model="form.timezone")
            option(v-for="tz in commonTimezones" :key="tz" :value="tz") {{ tz }}
        .form-error(v-if="submitError") {{ submitError }}
        button.confirm-btn(type="submit" :disabled="submitting")
          | {{ submitting ? 'Confirming...' : 'Confirm Booking' }}
      button.back-link(@click="step = 3") ← Back

    .step.success-step(v-if="step === 5")
      .success-icon ✓
      h2.success-title You're booked!
      .success-details
        p.detail-line {{ selectedType?.title }}
        p.detail-line {{ selectedDateFormatted }} at {{ formatTime(selectedSlot?.starts_at) }}
      p.success-email
        | A confirmation email has been sent to&nbsp;
        strong {{ form.email }}
        | .
      button.back-link(@click="resetBooking") ← Schedule another time
</template>

<style lang="stylus">
$booking-color = #111

main.booking-content
  position absolute
  top 0
  left 0
  right 0
  bottom 0
  background-color $booking-color
  pointer-events all
  display block
  opacity 0
  transition opacity 0.5s linear
  overflow-y auto

  .booking-inner
    max-width 680px
    margin 0 auto
    padding ($content-margin-top + $theme-margin-top) $theme-margin-left-right ($theme-margin-top * 2)
    color $theme-color-white
    font-family 'Raleway', sans-serif

  .step-title
    font-size ($golden-num ** 2)em
    font-weight 700
    margin-bottom 0.3em
    line-height 1.2

  .step-sub
    font-family $the-serif, serif
    font-size 1em
    font-weight 400
    color alpha($theme-color-white, 0.7)
    margin-bottom 1.5em

  .loading-msg,
  .error-msg
    color alpha($theme-color-white, 0.6)
    font-family $the-serif, serif

  .error-msg
    color $theme-color-pink

  /* Type cards */
  .type-cards
    display flex
    flex-direction column
    gap 1em

  .type-card
    border 1px solid alpha($theme-color-white, 0.15)
    border-radius 8px
    padding 1.2em 1.5em
    cursor pointer
    transition border-color 0.2s, background-color 0.2s

    &:hover
      border-color $theme-color-pink
      background-color alpha($theme-color-pink, 0.08)

    .type-duration
      font-size ($golden-num)em
      font-weight 700
      color $theme-color-pink
      margin-bottom 0.2em

    .type-name
      font-size 1em
      font-weight 600
      margin-bottom 0.4em

    .type-desc
      font-family $the-serif, serif
      font-size (1 / $golden-num)em
      color alpha($theme-color-white, 0.65)
      line-height 1.5

  /* Calendar */
  .calendar
    background alpha($theme-color-white, 0.04)
    border-radius 8px
    padding 1.2em
    max-width 420px
    margin-bottom 1.5em

  .cal-header
    display flex
    align-items center
    justify-content space-between
    margin-bottom 1em

  .cal-nav
    background none
    border none
    color $theme-color-white
    font-size 1.4em
    cursor pointer
    padding 0.2em 0.5em
    border-radius 4px
    transition color 0.2s

    &:hover
      color $theme-color-pink

  .cal-month
    font-weight 600
    font-size 0.95em
    letter-spacing 0.04em

  .cal-grid
    display grid
    grid-template-columns repeat(7, 1fr)
    gap 4px

  .day-label
    text-align center
    font-size 0.65em
    font-weight 600
    color alpha($theme-color-white, 0.4)
    padding 0.3em 0
    letter-spacing 0.06em

  .cal-day
    aspect-ratio 1
    background none
    border none
    border-radius 50%
    color $theme-color-white
    font-size 0.75em
    cursor pointer
    display flex
    align-items center
    justify-content center
    transition background-color 0.15s, color 0.15s

    &.empty
      pointer-events none

    &.past
      color alpha($theme-color-white, 0.25)
      cursor default

    &.available:hover
      background-color alpha($theme-color-pink, 0.2)

    &.today
      font-weight 700
      color $theme-color-pink

    &.selected
      background-color $theme-color-pink
      color $booking-color
      font-weight 700

    &:disabled
      cursor default

  /* Timeslots */
  .slots-grid
    display flex
    flex-wrap wrap
    gap 0.6em
    margin-bottom 1.5em

  .slot-btn
    background none
    border 1px solid alpha($theme-color-white, 0.25)
    border-radius 6px
    color $theme-color-white
    font-family 'Raleway', sans-serif
    font-size 0.8em
    padding 0.5em 1em
    cursor pointer
    transition border-color 0.2s, background-color 0.2s, color 0.2s

    &:hover
      border-color $theme-color-pink
      background-color alpha($theme-color-pink, 0.1)

    &.selected
      background-color $theme-color-pink
      border-color $theme-color-pink
      color $booking-color
      font-weight 700

  /* Form */
  .booking-summary
    font-family $the-serif, serif
    font-size (1 / $golden-num)em
    color alpha($theme-color-white, 0.7)
    margin-bottom 1.5em

    .dot
      color alpha($theme-color-white, 0.4)

  form
    display flex
    flex-direction column
    gap 1em

  .form-field
    display flex
    flex-direction column
    gap 0.4em

    label
      font-size 0.75em
      font-weight 600
      letter-spacing 0.06em
      color alpha($theme-color-white, 0.7)
      text-transform uppercase

    input,
    select
      background alpha($theme-color-white, 0.07)
      border 1px solid alpha($theme-color-white, 0.2)
      border-radius 6px
      color $theme-color-white
      font-family 'Raleway', sans-serif
      font-size 0.9em
      padding 0.6em 0.9em
      outline none
      transition border-color 0.2s

      &:focus
        border-color $theme-color-pink

      option
        background $booking-color
        color $theme-color-white

  .form-error
    color $theme-color-pink
    font-size 0.8em

  .confirm-btn
    background-color $theme-color-pink
    border none
    border-radius 6px
    color $booking-color
    cursor pointer
    font-family 'Raleway', sans-serif
    font-size 0.9em
    font-weight 700
    padding 0.8em 1.5em
    margin-top 0.5em
    transition opacity 0.2s
    align-self flex-start

    &:disabled
      opacity 0.5
      cursor not-allowed

    &:not(:disabled):hover
      opacity 0.85

  /* Success */
  .success-step
    text-align center
    padding-top 2em

  .success-icon
    font-size 3em
    color $theme-color-pink
    margin-bottom 0.3em

  .success-title
    font-size ($golden-num ** 2)em
    font-weight 700
    margin-bottom 0.8em

  .success-details
    font-family $the-serif, serif
    color alpha($theme-color-white, 0.8)
    margin-bottom 1em

    .detail-line
      margin 0.2em 0

  .success-email
    font-family $the-serif, serif
    font-size (1 / $golden-num)em
    color alpha($theme-color-white, 0.6)
    margin-bottom 1.5em

  /* Shared nav */
  .back-link
    background none
    border none
    color alpha($theme-color-white, 0.5)
    cursor pointer
    font-family $the-serif, serif
    font-size (1 / $golden-num)em
    margin-top 1em
    padding 0
    transition color 0.2s

    &:hover
      color $theme-color-white

  .no-slots
    margin-bottom 1em

    p
      font-family $the-serif, serif
      color alpha($theme-color-white, 0.7)
      margin-bottom 0.5em

  &.show
    opacity 1

  &.hide
    opacity 0
    pointer-events none

@media only screen and (max-width 400px)
  main.booking-content
    .booking-inner
      padding ($content-margin-top-mobile + $theme-margin-top-mobile) $theme-margin-left-right-mobile $theme-margin-top-mobile
</style>

<script setup lang="ts">
export interface BookingContentProps {
  show: boolean;
}

const props = defineProps<BookingContentProps>();

const classes = computed(() => ({
  show: props.show,
  hide: !props.show,
}));

const FUNCTION_URL = '/.netlify/functions/tidycal';

// Steps: 1=type, 2=calendar, 3=timeslots, 4=form, 5=success
const step = ref(1);

// Booking types
interface BookingType {
  id: number;
  title: string;
  description: string;
  duration_minutes: number;
  url_slug: string;
}

const bookingTypes = ref<BookingType[]>([]);
const loadingTypes = ref(false);
const typesError = ref(false);
const selectedType = ref<BookingType | null>(null);

// Calendar
const today = new Date();
const calYear = ref(today.getFullYear());
const calMonth = ref(today.getMonth()); // 0-indexed
const selectedDate = ref<Date | null>(null);

const monthYear = computed(() => {
  return new Date(calYear.value, calMonth.value, 1).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });
});

const calendarDays = computed(() => {
  const firstDay = new Date(calYear.value, calMonth.value, 1).getDay();
  const daysInMonth = new Date(calYear.value, calMonth.value + 1, 0).getDate();
  const todayStr = dateStr(today);
  const days: Array<{
    date: Date | null;
    label: string;
    key: string;
    isToday: boolean;
    isPast: boolean;
    isSelected: boolean;
  }> = [];

  for (let i = 0; i < firstDay; i++) {
    days.push({ date: null, label: '', key: `empty-${i}`, isToday: false, isPast: false, isSelected: false });
  }

  for (let d = 1; d <= daysInMonth; d++) {
    const date = new Date(calYear.value, calMonth.value, d);
    const ds = dateStr(date);
    const selDs = selectedDate.value ? dateStr(selectedDate.value) : '';
    days.push({
      date,
      label: String(d),
      key: ds,
      isToday: ds === todayStr,
      isPast: date < new Date(today.getFullYear(), today.getMonth(), today.getDate()),
      isSelected: ds === selDs,
    });
  }
  return days;
});

const selectedDateFormatted = computed(() => {
  if (!selectedDate.value) return '';
  return selectedDate.value.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
});

function prevMonth() {
  if (calMonth.value === 0) { calMonth.value = 11; calYear.value--; }
  else calMonth.value--;
}

function nextMonth() {
  if (calMonth.value === 11) { calMonth.value = 0; calYear.value++; }
  else calMonth.value++;
}

function dateStr(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Timeslots
interface Timeslot { starts_at: string; ends_at: string; available_bookings: number }
const timeslots = ref<Timeslot[]>([]);
const loadingSlots = ref(false);
const selectedSlot = ref<Timeslot | null>(null);

// Form
const form = ref({ name: '', email: '', timezone: Intl.DateTimeFormat().resolvedOptions().timeZone });
const submitting = ref(false);
const submitError = ref('');

const commonTimezones = [
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'America/Phoenix', 'America/Anchorage', 'Pacific/Honolulu',
  'America/Toronto', 'America/Vancouver', 'America/Mexico_City',
  'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Europe/Madrid',
  'Asia/Dubai', 'Asia/Kolkata', 'Asia/Singapore', 'Asia/Tokyo',
  'Australia/Sydney', 'Pacific/Auckland',
];

function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, '');
}

function formatTime(isoString?: string): string {
  if (!isoString) return '';
  return new Date(isoString).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: form.value.timezone,
  });
}

async function fetchBookingTypes() {
  loadingTypes.value = true;
  typesError.value = false;
  try {
    const res = await fetch(`${FUNCTION_URL}?action=booking-types`);
    const data = await res.json();
    bookingTypes.value = (data.data || []).filter((t: BookingType) => !t.disabled_at);
  } catch {
    typesError.value = true;
  } finally {
    loadingTypes.value = false;
  }
}

function selectType(type: BookingType) {
  selectedType.value = type;
  step.value = 2;
}

function selectDate(date: Date) {
  selectedDate.value = date;
  step.value = 3;
  fetchTimeslots(date);
}

async function fetchTimeslots(date: Date) {
  loadingSlots.value = true;
  timeslots.value = [];
  selectedSlot.value = null;
  const ds = dateStr(date);
  const starts = `${ds}T00:00:00Z`;
  const ends = `${ds}T23:59:59Z`;
  try {
    const res = await fetch(
      `${FUNCTION_URL}?action=timeslots&bookingTypeId=${selectedType.value!.id}&starts_at=${starts}&ends_at=${ends}`
    );
    const data = await res.json();
    timeslots.value = (data.data || []).filter((s: Timeslot) => s.available_bookings > 0);
  } catch {
    timeslots.value = [];
  } finally {
    loadingSlots.value = false;
  }
}

function selectSlot(slot: Timeslot) {
  selectedSlot.value = slot;
  step.value = 4;
}

async function submitBooking() {
  submitting.value = true;
  submitError.value = '';
  try {
    const res = await fetch(
      `${FUNCTION_URL}?action=create-booking&bookingTypeId=${selectedType.value!.id}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          starts_at: selectedSlot.value!.starts_at,
          name: form.value.name,
          email: form.value.email,
          timezone: form.value.timezone,
        }),
      }
    );
    if (!res.ok) {
      const err = await res.json();
      submitError.value = err.message || 'Something went wrong. Please try again.';
    } else {
      step.value = 5;
    }
  } catch {
    submitError.value = 'Network error. Please try again.';
  } finally {
    submitting.value = false;
  }
}

function resetBooking() {
  step.value = 1;
  selectedType.value = null;
  selectedDate.value = null;
  selectedSlot.value = null;
  timeslots.value = [];
  form.value = { name: '', email: '', timezone: Intl.DateTimeFormat().resolvedOptions().timeZone };
  submitError.value = '';
}

watch(
  () => props.show,
  (newVal) => {
    if (newVal && bookingTypes.value.length === 0) {
      fetchBookingTypes();
    }
  }
);
</script>
